import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import moment from 'moment'
import { dateWindowFlag, mapMaxDateRangeDays, defaultDateRange } from './date-window'

type ApprovalStatus = 'Approved' | 'PendingApproval' | 'NoApproval'

export const useJobFilterStore = defineStore(
  'job-filters',
  () => {
    const isMigratedFromVuex = ref<boolean>(false)
    const isShowingFilters = ref<boolean>(false)
    const search = ref<string>('')
    const selectedPriorities = ref<string[]>([])
    const selectedDivisions = ref<string[]>([])
    const selectedServiceProviders = ref<(string | null)[]>([])
    const selectedResourceTypes = ref<number[]>([])
    const selectedActivities = ref<number[]>([])
    const selectedJobStatuses = ref<string[]>([])
    const selectedBatchStatuses = ref<string[]>([])
    const selectedApprovalStatuses = ref<ApprovalStatus[]>([])
    const selectedLocations = ref<number[]>([])
    // IA-586 rollout: off keeps the fixed week-to-today default, its label and no reset.
    const dateWindowEnabled = computed(() => dateWindowFlag.value === true)
    const maxDateRangeDays = computed(() => mapMaxDateRangeDays.value)
    const currentDefault = () =>
      dateWindowEnabled.value
        ? defaultDateRange(true, maxDateRangeDays.value)
        : defaultDateRange(false)
    const initialRange = currentDefault()
    const start = ref<moment.Moment | null>(initialRange.start)
    const end = ref<moment.Moment | null>(initialRange.end)
    // A picked range stays put; the default rolls forward with the calendar.
    const isDefaultDateRange = ref<boolean>(true)

    // NOTE: Field View Filters (separate from the old job list filters
    // above).
    //
    // These are temporary and will be removed once we get started on
    // Unified Headers.
    const fieldViewSearch = ref<string>('')
    const selectedFieldViewPriorities = ref<string[]>([])
    const selectedFieldViewDivisions = ref<string[]>([])
    const selectedFieldViewServiceProviders = ref<(string | null)[]>([])
    const selectedFieldViewResourceTypes = ref<number[]>([])
    const selectedFieldViewActivities = ref<number[]>([])
    const selectedFieldViewJobStatuses = ref<string[]>([])
    const selectedFieldViewBatchStatuses = ref<string[]>([])
    const selectedFieldViewApprovalStatuses = ref<ApprovalStatus[]>([])
    const selectedFieldViewLocations = ref<number[]>([])
    const fieldViewStart = ref<moment.Moment | null>(moment().startOf('day').subtract(7, 'days'))
    const fieldViewEnd = ref<moment.Moment | null>(moment().endOf('day'))

    const hasDateRange = computed(() => {
      return start.value !== null && end.value !== null
    })

    const dateRangeLabel = computed(() => {
      if (!hasDateRange.value) return 'Showing all dates'
      if (!dateWindowEnabled.value)
        return `${end.value!.diff(start.value, 'days') + 1} dates selected`
      const format = start.value!.year() === end.value!.year() ? 'MMM D' : 'MMM D, YYYY'
      return `${start.value!.format(format)} – ${end.value!.format(format)}`
    })

    const showFilterPanel = () => {
      isShowingFilters.value = true
    }

    const hideFilterPanel = () => {
      isShowingFilters.value = false
    }

    const clearDateRange = () => {
      start.value = null
      end.value = null
      isDefaultDateRange.value = false
    }

    const resetDateRange = () => {
      const range = currentDefault()
      start.value = range.start
      end.value = range.end
      isDefaultDateRange.value = true
    }

    // Moves the default window on once the calendar day changes (or a saved default has
    // stale endpoints); picked ranges are left alone.
    const rollDefaultDateRange = () => {
      if (!dateWindowEnabled.value || !isDefaultDateRange.value) return
      const range = currentDefault()
      if (!start.value?.isSame(range.start, 'day') || !end.value?.isSame(range.end, 'day')) {
        resetDateRange()
      }
    }

    const updateDateRange = (range: { start: moment.Moment | null; end: moment.Moment | null }) => {
      start.value = range.start
      end.value = range.end
      isDefaultDateRange.value = false
    }

    const updateSearch = (input: string) => {
      search.value = input
    }

    const updatePriority = (priorities: string[]) => {
      selectedPriorities.value = priorities
    }

    const updateDivision = (divisions: string[]) => {
      selectedDivisions.value = divisions
    }

    const updateServiceProvider = (serviceProviders: string[]) => {
      selectedServiceProviders.value = serviceProviders
    }

    const updateResourceType = (resourceTypes: number[]) => {
      selectedResourceTypes.value = resourceTypes
    }

    const updateActivity = (activities: number[]) => {
      selectedActivities.value = activities
    }

    const updateJobStatus = (jobStatuses: string[]) => {
      selectedJobStatuses.value = jobStatuses
    }

    const updateBatchStatus = (batchStatuses: string[]) => {
      selectedBatchStatuses.value = batchStatuses
    }

    const updateApprovalStatus = (approvalStatuses: ApprovalStatus[]) => {
      selectedApprovalStatuses.value = approvalStatuses
    }

    const updateLocation = (locations: number[]) => {
      selectedLocations.value = locations
    }

    const resetAllFilters = () => {
      search.value = ''
      resetDateRange()
      selectedPriorities.value = []
      selectedDivisions.value = []
      selectedServiceProviders.value = []
      selectedResourceTypes.value = []
      selectedActivities.value = []
      selectedJobStatuses.value = []
      selectedBatchStatuses.value = []
      selectedApprovalStatuses.value = []
      selectedLocations.value = []
    }

    return {
      isMigratedFromVuex,
      isShowingFilters,
      search,
      start,
      end,
      isDefaultDateRange,
      dateWindowEnabled,
      maxDateRangeDays,
      selectedPriorities,
      selectedDivisions,
      selectedServiceProviders,
      selectedResourceTypes,
      selectedActivities,
      selectedJobStatuses,
      selectedBatchStatuses,
      selectedApprovalStatuses,
      selectedLocations,
      fieldViewSearch,
      selectedFieldViewPriorities,
      selectedFieldViewDivisions,
      selectedFieldViewServiceProviders,
      selectedFieldViewResourceTypes,
      selectedFieldViewActivities,
      selectedFieldViewJobStatuses,
      selectedFieldViewBatchStatuses,
      selectedFieldViewApprovalStatuses,
      selectedFieldViewLocations,
      fieldViewStart,
      fieldViewEnd,
      hasDateRange,
      dateRangeLabel,
      showFilterPanel,
      hideFilterPanel,
      clearDateRange,
      resetDateRange,
      rollDefaultDateRange,
      updateDateRange,
      updateSearch,
      updatePriority,
      updateDivision,
      updateServiceProvider,
      updateResourceType,
      updateActivity,
      updateJobStatus,
      updateBatchStatus,
      updateApprovalStatus,
      updateLocation,
      resetAllFilters,
    }
  },
  {
    persist: {
      afterRestore: (ctx) => {
        ctx.store.start = ctx.store.start != null ? moment(ctx.store.start) : null
        ctx.store.end = ctx.store.end != null ? moment(ctx.store.end) : null
        ctx.store.fieldViewStart =
          ctx.store.fieldViewStart != null ? moment(ctx.store.fieldViewStart) : null
        ctx.store.fieldViewEnd =
          ctx.store.fieldViewEnd != null ? moment(ctx.store.fieldViewEnd) : null
        ctx.store.rollDefaultDateRange()
      },
    },
  },
)
